import type { CognitoUserSession, ICognitoUserPoolData } from 'amazon-cognito-identity-js'
import { AuthenticationDetails, CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js'

const poolData: ICognitoUserPoolData = {
  ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
  UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
}

const UserPool = new CognitoUserPool(poolData)

let hoistedCognitoUser: CognitoUser | undefined
let sessionUserAttributes = undefined as any

function createCognitoUser(email: string): CognitoUser {
  return new CognitoUser({ Pool: UserPool, Username: email })
}

function getSession(user: CognitoUser): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    user.getSession((error: Error | null, session: CognitoUserSession) => {
      if (error || !session.isValid()) {
        const message = error || 'Session not valid'
        return reject(message)
      }

      resolve(session)
    })
  })
}

export function serviceChangePassword(oldPassword: string, newPassword: string) {
  return new Promise((resolve, reject) => {
    const cognitoUser = UserPool.getCurrentUser()
    if (cognitoUser !== null) {
      cognitoUser.changePassword(oldPassword, newPassword, (err, result) => {
        if (err) {
          reject(err)
        }
        resolve(result)
      })
    }
  })
}

export function serviceChangeTemporaryPassword(password: string) {
  return new Promise((resolve, reject) => {
    try {
      if (hoistedCognitoUser !== null && hoistedCognitoUser !== undefined) {
        hoistedCognitoUser.completeNewPasswordChallenge(password, sessionUserAttributes, {
          onSuccess: (result: any) => {
            sessionUserAttributes = undefined
            hoistedCognitoUser = undefined
            resolve(result)
          },
          onFailure: (error: any) => {
            reject(error)
          },
        })
      }
    }
    catch (error) {
      reject(error)
    }
  })
}

export function serviceConfirmCodeSetNewPassword(email: string, verificationCode: string, newPassword: string) {
  return new Promise((resolve, reject) => {
    const newCognitoUser = createCognitoUser(email)
    try {
      newCognitoUser.confirmPassword(verificationCode, newPassword, {
        onFailure(err) {
          reject(err)
        },
        onSuccess(result) {
          resolve(result)
        },
      })
    }
    catch (error) {
      reject(error)
    }
  })
}

export function serviceGetUserToken(): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const cognitoUser = UserPool.getCurrentUser()

    if (cognitoUser !== null) {
      getSession(cognitoUser).then(
        session => resolve(session.getIdToken().getJwtToken()),
        async (error) => {
          if (error.message === 'User does not exist.') {
            cognitoUser.signOut()
            return reject(new Error('No current user'))
          }
          reject(error)
        },
      )
    }
  })
}

export function serviceIsLoggedIn() {
  const cognitoUser = UserPool.getCurrentUser()

  return cognitoUser !== null
}

export function serviceLogin(email: string, password: string) {
  return new Promise((resolve, reject) => {
    // Create user representation.
    const user = createCognitoUser(email)

    // Create the auth with user + password.
    const authDetails = new AuthenticationDetails({
      Password: password,
      Username: email,
    })

    // Authenticate, handle callbacks
    user.authenticateUser(authDetails, {
      onSuccess: (result: CognitoUserSession) => {
        const accessToken = result.getAccessToken().getJwtToken()

        const response = {
          result,
          accessToken,
        }

        resolve(response)
      },
      onFailure: (err: any) => {
        console.error(err)
        reject(err)
      },
      newPasswordRequired: (userAttributes: any) => {
        // The api doesn't accept this field back
        // https://stackoverflow.com/a/71680121/21403002
        delete userAttributes.email
        delete userAttributes.email_verified

        // Store userAttributes in accessible variable
        sessionUserAttributes = userAttributes

        // Store user in accessible variable
        hoistedCognitoUser = user
        const response = {
          result: {
            redirect: 'auth-change-password',
          },
        }
        resolve(response)
      },
    })
  })
}

export function serviceLogout(email: string | undefined) {
  return new Promise((resolve, reject) => {
    let user = UserPool.getCurrentUser()

    if (!user && email) {
      user = createCognitoUser(email)
    }

    if (user) {
      try {
        user.signOut()
        resolve(true)
      }
      catch (err) {
        reject(err)
      }
    }
    else {
      reject(new Error('No user to sign out'))
    }
  })
}

export function serviceRequestPasswordResetCode(email: string) {
  return new Promise((resolve, reject) => {
    // Create user representation.
    const cognitoUser = createCognitoUser(email)

    try {
      // call forgotPassword on cognitoUser
      cognitoUser.forgotPassword({
        onSuccess: (result) => {
          resolve(result)
        },
        onFailure: (err) => {
          reject(err)
        },
      })
    }
    catch (err) {
      reject(err)
    }
  })
}
